import { AlertCircle } from 'lucide-react';
import { useState } from 'react';
import { useLocation, useNavigate, useRouteError } from 'react-router-dom';
import { EmptyState } from '@/components/EmptyState';
import { buttonClasses } from '@/components/Button';
import { useT } from '@/i18n/LocaleProvider';
import { RouteChunkLoadError } from './lazyRoute';
import {
  browserModuleReader,
  refreshModuleGraph,
  type GraphRefreshType,
} from './moduleGraphRefresh';

const STACK_LINES = 8;
/** How long Try again waits on the refresh before reloading anyway: the network may be down still. */
const REFRESH_DEADLINE_MS = 8_000;

/**
 * I-1110: what whoever reports "This page ran into a problem" can paste: the error's name and
 * message, the route's path (never its query or hash, which can carry a token) and the top of the
 * stack. The console has the full error, but a phone or a tester's screen has no console.
 */
export function routeErrorDetails(error: unknown, pathname: string): string {
  const named = error instanceof Error ? error : null;
  const head = named ? `${named.name}: ${named.message}` : `Error: ${String(error)}`;
  const stack = (named?.stack ?? '')
    .split('\n')
    .filter((line) => /^\s+at |@/.test(line))
    .slice(0, STACK_LINES);
  return [head, `Route: ${pathname}`, ...stack].join('\n');
}

/**
 * What a route shows when it cannot be drawn, in place of React Router's developer error screen
 * (I-0678).
 *
 * Mounted as an `errorElement` inside the layout, so the navigation and the header stay where they
 * are and only the page area says what went wrong. A screen whose code did not arrive says so and
 * offers Try again; any other failure gets the same calm shape. Neither shows a stack trace: the
 * reader cannot act on one, and the console still has it.
 */
/** The page's reload, and the refresh before it (I-1522), as seams the unit test replaces. */
export const routeLoadErrorPage = {
  reload: () => window.location.reload(),
  async refresh(url: string): Promise<GraphRefreshType['outcome']> {
    const controller = new AbortController();
    const deadline = setTimeout(() => controller.abort(), REFRESH_DEADLINE_MS);
    try {
      return (await refreshModuleGraph(url, browserModuleReader(controller.signal))).outcome;
    } finally {
      clearTimeout(deadline);
    }
  },
};

/**
 * I-1522: the screen's imports are asked for again first, and the page reloads only once they came
 * back. WebKit keeps a module that failed in a memory cache that outlives the reload, and the
 * reloaded page failed the same import without asking the network; a request for it replaces that
 * record. When a module still cannot be had, a reload would draw this same error, so the reader is
 * told and the page stays. A module the server no longer has is a new build, which a reload fetches.
 * A failure that names no chunk has nothing to ask for, and reloads as before.
 */
async function refreshAndReload(url: string | null): Promise<'reloading' | 'unreachable'> {
  let outcome: GraphRefreshType['outcome'] = 'fresh';
  try {
    if (url) outcome = await routeLoadErrorPage.refresh(url);
  } catch {
    outcome = 'failed';
  }
  if (outcome === 'failed') return 'unreachable';
  routeLoadErrorPage.reload();
  return 'reloading';
}

export function RouteLoadError() {
  const error = useRouteError();
  const navigate = useNavigate();
  const location = useLocation();
  const t = useT();
  const chunk = error instanceof RouteChunkLoadError;
  const [attempt, setAttempt] = useState<'idle' | 'refreshing' | 'reloading' | 'unreachable'>(
    'idle',
  );
  const refreshing = attempt === 'refreshing' || attempt === 'reloading';

  const tryAgain = () => {
    /*
      T-0548: a screen whose code did not arrive is fetched again by reloading the page. Asked again
      inside the page, it fails again whenever what failed was one of the screen's own imports: the
      browser keeps a failed module for as long as the page lives, and the retry's fresh address is
      the screen's chunk, not its dependency's (measured in WebKit and Chromium: one dropped
      dependency, then Try again, and the error stayed). A reload is the reader's own act here, on
      the same address, so it cannot loop the way an automatic one could. In WebKit the reload alone
      is not enough (I-1522): see `refreshAndReload`.
    */
    if (chunk) {
      setAttempt('refreshing');
      void refreshAndReload(error.url).then(setAttempt);
      return;
    }
    // A new location is what clears a route's error, so the same address is visited again.
    navigate(`${location.pathname}${location.search}${location.hash}`, { replace: true });
  };

  return (
    <div role="alert">
      <EmptyState
        icon={<AlertCircle className="h-8 w-8" strokeWidth={1.5} aria-hidden="true" />}
        title={chunk ? t('navigation.routeError.chunkTitle') : t('navigation.routeError.pageTitle')}
        description={
          chunk ? t('navigation.routeError.chunkBody') : t('navigation.routeError.pageBody')
        }
        action={
          <div className="flex max-w-sm flex-col items-center gap-3">
            <button
              type="button"
              onClick={tryAgain}
              disabled={refreshing}
              aria-busy={refreshing || undefined}
              className={buttonClasses({ size: 'sm' })}
            >
              {refreshing ? t('navigation.routeError.tryingAgain') : t('navigation.tryAgain')}
            </button>
            {/* I-1522: the refresh found a module still out of reach, so the page stayed rather
                than reloading into this same screen. Under the button it answers. */}
            <p role="status" className="text-[13px] text-red-700 empty:hidden dark:text-red-200">
              {attempt === 'unreachable' ? t('navigation.routeError.unreachable') : ''}
            </p>
          </div>
        }
      />
      {/* Collapsed, the same disclosure a failed load has (`LoadFailureBanner`). */}
      <details className="mx-auto mt-2 max-w-md px-4 text-[12px]" data-testid="route-error-details">
        <summary className="cursor-pointer text-center">
          {t('navigation.routeError.technicalDetails')}
        </summary>
        <pre className="mt-1 whitespace-pre-wrap break-all font-mono text-[11px]">
          {routeErrorDetails(error, location.pathname)}
        </pre>
      </details>
    </div>
  );
}
